import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@store/index';
import {
  setAnalysisError,
  setAnalysisResults,
  setCurrentSession,
  setWizardStep,
} from '@features/drbfm/drbfmSlice';
import { drbfmService } from '@services/drbfm.service';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';

interface SessionAutoLoadArgs {
  sessionIdParam: string | null;
  isCaseMode: boolean;
  isStagedMode?: boolean;
  reset: () => void;
}

/**
 * When arriving from the history list via `/drbfm?session=<id>`, hydrate the
 * wizard from that session: load metadata + results, then jump to the results
 * step (DRBFMResultsStep). Without this the page mounts on Step 1 with an empty
 * form and the "結果を表示" action looked broken (TG #673).
 *
 * The loader lives here (instead of useDRBFM.loadSession) so we can guard each
 * Redux dispatch against the URL changing mid-fetch. The returned ref tracks the
 * currently-targeted session id; any await whose surrounding request id no
 * longer matches the ref bails before touching Redux.
 */
export const useDrbfmSessionAutoLoad = ({
  sessionIdParam,
  isCaseMode,
  isStagedMode = false,
  reset,
}: SessionAutoLoadArgs) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const sessionAutoLoadRef = useRef<string | null>(null);

  useEffect(() => {
    if (isCaseMode || isStagedMode) {
      sessionAutoLoadRef.current = null;
      return;
    }

    if (!sessionIdParam) {
      // Bare /drbfm always returns to a clean slate. The earlier ref-only check
      // failed on remount (e.g. /drbfm/history → /drbfm re-mounts the component
      // with ref=null), so we now reset unconditionally.
      sessionAutoLoadRef.current = null;
      reset();
      return;
    }
    if (sessionAutoLoadRef.current === sessionIdParam) return;
    sessionAutoLoadRef.current = sessionIdParam;
    const requested = sessionIdParam;
    let active = true;
    // Clear any previously-loaded session before kicking off the new request.
    // Without this, a quick switch from /drbfm?session=B to /drbfm?session=A
    // briefly renders B's results, export, and 3D buttons under A's URL.
    reset();
    void (async () => {
      try {
        const session = await drbfmService.getSession(requested);
        if (!active || sessionAutoLoadRef.current !== requested) return;
        dispatch(setCurrentSession(session));
        if (session.status === 'completed') {
          const results = await drbfmService.getSessionResults(requested);
          if (!active || sessionAutoLoadRef.current !== requested) return;
          dispatch(setAnalysisResults(results));
          dispatch(setWizardStep('results'));
        } else if (session.status === 'draft') {
          dispatch(setWizardStep('input'));
        } else {
          dispatch(setWizardStep('input'));
          dispatch(setAnalysisError(t(session.status === 'analyzing'
            ? 'drbfm.firstRun.analysisInProgress' : 'drbfm.firstRun.analysisFailed')));
        }
      } catch (err) {
        if (!active || sessionAutoLoadRef.current !== requested) return;
        // Clear stale state from the previous successfully-loaded session so the
        // user doesn't keep seeing/exporting old results under a URL whose
        // target couldn't be fetched.
        reset();
        dispatch(setAnalysisError(
          resolveApiErrorMessage(t, err, { fallbackKey: 'drbfm.errors.sessionLoadFailed' }),
        ));
      }
    })();
    return () => {
      active = false;
      if (sessionAutoLoadRef.current === requested) sessionAutoLoadRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the fetch-error message; excluded so a language change doesn't reset() the in-progress wizard (PR #790)
  }, [isCaseMode, isStagedMode, sessionIdParam, dispatch, reset]);

  return sessionAutoLoadRef;
};
